import { HttpErrorResponse } from '@angular/common/http';

import { ImportConfirmState } from '../models/import-confirm-state';

/**
 * What a failed import Confirm means (M&N, Oct 2026, D-7).
 *
 * ⚠️ An import Confirm is one long request, and the server carries on with it whatever happens to
 * the connection. So "the request failed" is NOT "the import failed": a 219-piece delivery note
 * said "Import failed" while the server was still receiving it, and 371 seconds later every piece
 * was in. Only an answer the API itself gave can be read as an outcome:
 * - a 4xx is a refusal - nothing was done, and the body says why;
 * - a 500 carrying the API's own `{ message, details }` body is a failure the API caught;
 * - anything else - no answer at all (status 0), 502/503/504, or any error whose body is an HTML page
 *   (the hosting front end answers a request it gave up waiting on with a page of its own) - is
 *   UNKNOWN: the import may still be running, or may have finished.
 * Something that never became an HTTP answer at all (an interceptor swallowing a 404) is a failure:
 * the import never ran.
 *
 * The message for the user is the caller's - this returns the status and the server's own text.
 */
export function importConfirmFailure(error: unknown): ImportConfirmState {
  if (!(error instanceof HttpErrorResponse)) {
    return { status: 'failed' };
  }

  const status = error.status ?? 0;
  if (status === 0 || status === 502 || status === 503 || status === 504) {
    return { status: 'unknown' };
  }

  const body = parsedBody(error.error);
  if (typeof body === 'string' && body.trim().startsWith('<')) {
    return { status: 'unknown' };
  }

  if (status >= 400 && status < 500) {
    return { status: 'refused', detail: bodyMessage(body) };
  }

  if (status === 500 && isApiErrorBody(body)) {
    return { status: 'failed', detail: bodyMessage(body) };
  }

  return { status: 'unknown' };
}

// The confirm is posted for TEXT, so a JSON body arrives as a string: a refusal as "\"…\"" or
// plain text, the API's 500 as "{\"message\":…}".
function parsedBody(body: unknown): unknown {
  if (typeof body !== 'string') {
    return body;
  }

  try {
    return JSON.parse(body);
  } catch {
    return body;
  }
}

function isApiErrorBody(body: unknown): boolean {
  return !!body && typeof body === 'object' && 'message' in body && 'details' in body;
}

function bodyMessage(body: unknown): string | undefined {
  if (typeof body === 'string') {
    return body.trim() || undefined;
  }

  const message = body && typeof body === 'object' ? (body as Record<string, unknown>)['message'] : undefined;
  return typeof message === 'string' && message.trim() ? message.trim() : undefined;
}
